/**
 * EV Post-Close Rate Lock API — the `/pricing/post_close/*` routes on the
 * eastview-los backend (same host + Bearer auth as the rest of the LOS API).
 *
 * These are the post-close peer of the pre-close lock flow (`losLocksApi`,
 * `/pricing/*`). Population: EV Post-Close loans only (Salesforce RecordType
 * `EV_Post_Close`, `Loan_Id_Calculated__c LIKE 'EV%'`), keyed by Salesforce
 * Opportunity Id. On confirm, an idempotent `Rate_Lock__c` is inserted on the SF
 * Opportunity. One active lock per loan; a new request supersedes the prior one.
 *
 * Auth notes (enforced server-side):
 *   - every call needs the Bearer token (losHttp attaches it) → 401 without.
 *   - confirm_lock + all_locks are capital-markets only → 403 otherwise.
 *   - search / loan / price_preview / request_lock / cancel_lock: any authed user.
 *
 * Value scales (IMPORTANT): lock rate inputs are DECIMALS (0.085 = 8.5%); DSCR
 * prices are bond points (par = 100). The Salesforce `noteRate` on a search/loan
 * result is a WHOLE percent (9.75 = 9.75%) — convert with `noteRateToDecimal`
 * before formatting it like a lock rate.
 */
import isNil from "lodash/isNil";

import { losHttp } from "../lib/losHttp";

const BASE = "/pricing/post_close";

// ── enums / option lists ─────────────────────────────────────────────────────
export const POST_CLOSE_LOCK_PRODUCTS = { DSCR: "dscr", RTL: "rtl" };

// Lock workflow states.
export const POST_CLOSE_LOCK_STATUSES = [
  "Requested",
  "Confirmed",
  "Cancelled",
  "Expired",
  "Repriced",
  "Superseded",
];
// A lock is "active" (the one acted on) while in one of these states.
export const POST_CLOSE_ACTIVE_LOCK_STATUSES = ["Requested", "Confirmed", "Repriced"];

// DSCR margin tiers (server falls back to Gold 0.50 / Silver 0.75 / Bronze 1.00
// when margin_points_at_lock is omitted).
export const POST_CLOSE_DSCR_MARGIN_TIERS = ["Gold", "Silver", "Bronze"];

// Salesforce restricted picklists — values outside these sets are dropped on the
// SF push (the lock still saves), so the UI hints them but doesn't hard-require.
export const SF_COUNTERPARTY_CODES = [
  "BAR", "BF", "JPM", "MS", "NM", "PRT", "RW", "SG", "VR",
];
export const SF_LOCK_PERIOD_DAYS = [30, 45, 60];

/**
 * Resolve the Salesforce Opportunity Id off an LOS loan, matching the backend's
 * own order (salesforceOpportunityId → integration_id → salesforce_opportunity_id).
 * Returns null when the loan isn't linked to a Salesforce opportunity yet.
 */
export const resolvePostCloseOppId = (loan) =>
  loan?.salesforceOpportunityId ||
  loan?.integration_id ||
  loan?.salesforce_opportunity_id ||
  null;

/**
 * Is this drilldown loan an EV Post-Close loan?
 *
 * Post-close loans are NOT preclose pipeline deals: an `EV_Post_Close` Salesforce
 * opportunity syncs into `loan_acquisitions` (the ACQUISITIONS pipeline), while
 * `underwriting_pipeline` is the `EV_Pre_Close` side. The backend then scopes
 * `/pricing/post_close/*` to `RecordType = EV_Post_Close` AND
 * `Loan_Id_Calculated__c LIKE 'EV%'` (`app/models/post_close_lock.py::_population_where`),
 * whose LOS mirror is the acquisition's `assetId` / `sync_loan_id`.
 *
 * So: acquisitions pipeline (the caller checks that with `useIsAcquisitions()`) +
 * an EV loan id + a linked opportunity. Do NOT gate on a "Closed" status — the
 * pipeline-grid predicate (`PIPELINE_TABS.POST_CLOSE.match`) matches every funded
 * preclose deal, which sends loans that aren't in the population to the Salesforce
 * lookup and 404s them ("Loan not found in the EV_Post_Close population").
 */
export const isPostCloseLoan = (loan) => {
  const loanId =
    loan?.assetId || loan?.sync_loan_id || loan?.syncLoanId || loan?.loanId || "";
  if (!/^EV/i.test(String(loanId))) return false;
  return Boolean(resolvePostCloseOppId(loan));
};

/** DSCR else RTL — mirrors the backend's auto-derivation from the loan program. */
export const postCloseProductForProgram = (program) =>
  /dscr/i.test(String(program || "")) ? "dscr" : "rtl";

/** SF noteRate (whole percent, e.g. 9.75) → decimal (0.0975) for lock-rate formatting. */
export const noteRateToDecimal = (v) => (isNil(v) ? null : Number(v) / 100);

export const postCloseLockApi = {
  // GET /pricing/post_close/search?q=&limit= — typeahead over EV_Post_Close.
  // Empty q → most-recent loans. → { count, results: [opp] }
  search: (q = "", limit) =>
    losHttp
      .get(`${BASE}/search`, {
        params: { q, ...(isNil(limit) ? {} : { limit }) },
      })
      .then((r) => r.data),

  // GET /pricing/post_close/loan/{opportunityId} — one loan's context + its
  // current active lock. → { loan, activeLock|null }  (404 if not EV_Post_Close)
  getLoan: (opportunityId) =>
    losHttp.get(`${BASE}/loan/${opportunityId}`).then((r) => r.data),

  // GET /pricing/post_close/all_locks (capital_markets) — every post-close lock
  // doc, for the queue grid. → array of lock docs
  allLocks: () => losHttp.get(`${BASE}/all_locks`).then((r) => r.data),

  // POST /pricing/post_close/price_preview — compute prices without saving.
  // FLAT body: { product, lock_channel? (Direct|Broker|Correspondent — the legacy
  // is_correspondent_lock is derived from it), ...price inputs }.
  //   DSCR → { total_exit_price, lock_total_premium_points, actual_margin,
  //            exit_price_to_correspondent? }
  //   RTL  → { lock_buy_rate, lock_rate_spread }
  pricePreview: (body) =>
    losHttp.post(`${BASE}/price_preview`, body).then((r) => r.data),

  // POST /pricing/post_close/request_lock/{opportunityId} — MULTIPART (mirrors
  // pre-close request_dscr_lock), even with no sizer. `submission` is a JSON
  // string of the FLAT price inputs + options (product?, lock_period_days?,
  // lock_channel?, lock_notes?, price inputs, and — only when a file is
  // sent — uploadMetadata[{ id: file.name, docType, docGroup }]). `uploadFiles`
  // is an optional sizer .xlsx. Do NOT set Content-Type — the browser sets the
  // multipart boundary. → created lock doc (lock_status "Requested").
  requestLock: (opportunityId, submission, file) => {
    const fd = new FormData();
    fd.append("submission", JSON.stringify(submission || {}));
    if (file) fd.append("uploadFiles", file, file.name);
    return losHttp
      .post(`${BASE}/request_lock/${opportunityId}`, fd)
      .then((r) => r.data);
  },

  // POST /pricing/post_close/confirm_lock/{opportunityId} (capital_markets) —
  // confirm the active lock + insert Rate_Lock__c in SF. All fields optional:
  // { lock_period_days?, lock_expiration_date?, lock_counterparty?,
  //   lock_channel?, lock_notes? }. Locked_by is derived from the
  // caller server-side. → { lock, action, sf_rate_lock_id }
  confirmLock: (opportunityId, body) =>
    losHttp
      .post(`${BASE}/confirm_lock/${opportunityId}`, body || {})
      .then((r) => r.data),

  // POST /pricing/post_close/cancel_lock/{opportunityId} — { reason? }.
  cancelLock: (opportunityId, body) =>
    losHttp
      .post(`${BASE}/cancel_lock/${opportunityId}`, body || {})
      .then((r) => r.data),
};

export default postCloseLockApi;
